"use client";

import { create } from "zustand";
import { persist } from "zustand/middleware";
import dayjs from "dayjs";

import {
  defaultDelivery,
  defaultSubscription,
  defaultUser,
  parseDirectSalesPersistedSnapshot,
  type TCatFormDataInput,
  type TCatEntry,
  type TDirectSalesStoreData,
  type TDirectSalesViewState,
  type TDirectSalesCatDetailsViewState,
  type TDirectSalesCheckoutViewState,
} from "@./suf-store";
import { buildBaseStoreSlice } from "../../../shared/storeHelpers";

import { TargetUser, type IDirectSalesSignupFlowStore } from "../types";
import {
  defaultDirectSalesViewState,
  DIRECT_SALES_FLOW_TTL_DAYS,
} from "../constants";
import { useDirectSalesFlowErrorStore } from "./useDirectSalesFlowErrorStore";
import {
  DEFAULT_DIRECT_SALES_QUIZ_VERSION,
} from "@api-clients/suf-quiz-answers";

/**
 * Zustand store for managing direct sales sign-up flow data: cats, user, delivery, and subscription.
 */
export const useDirectSalesFlowStore = create<IDirectSalesSignupFlowStore>()(
  persist(
    (set, get) => ({
      ...buildBaseStoreSlice(set, get),

      createCat: (id, cat) => set((state) => {
        if (state.cats[id]) { return {}; }
        return {
          cats: {
            ...state.cats,
            [id]: { ...cat },
          },
          viewState: {
            ...state.viewState,
          },
        };
      }),

      createCats: (entries: TCatEntry[]) => set((state) => {
        const updatedCats: Record<string, TCatFormDataInput> = { ...state.cats };
        const incomingIds = new Set(entries.map((e) => e.id));

        // Remove cats that are no longer in the incoming entries
        for (const existingId of Object.keys(updatedCats)) {
          if (!incomingIds.has(existingId)) {
            delete updatedCats[existingId];
          }
        }

        // Add/update cats
        for (const { id, cat } of entries) {
          updatedCats[id] = {
            ...updatedCats[id],
            ...cat,
          };
        }

        const firstCatId = entries[0]?.id ?? "";

        return {
          cats: updatedCats,
          activeCatId: firstCatId,
          viewState: {
            ...state.viewState,
          },
        };
      }),

      /* --- Card Reader ---*/
      cardReaderId: "",
      setCardReaderId: (id: string) => set(() => ({
        cardReaderId: id,
      })),

      /* --- Target user ---*/
      targetUser: TargetUser.CUSTOMER,
      setTargetUser: (target: TargetUser) => set(() => ({
        targetUser: target,
      })),

      /* --- View State ---*/
      viewState: defaultDirectSalesViewState,
      updateViewState: (
        view: keyof TDirectSalesViewState,
        params: Partial<TDirectSalesViewState[keyof TDirectSalesViewState]>
        | Partial<TDirectSalesCatDetailsViewState>,
        catId?: string,
      ) => set((state) => {
        const viewState = { ...state.viewState };

        if (view === "catDetails" && catId) {
          // Update catDetails viewState for specific cat
          viewState.catDetails = {
            ...viewState.catDetails,
            [catId]: {
              ...viewState.catDetails[catId],
              ...params,
            } as TDirectSalesCatDetailsViewState,
          };
        }

        if (view === "checkout") {
          viewState.checkout = {
            ...viewState.checkout,
            ...params,
          } as TDirectSalesCheckoutViewState;
        }

        return { viewState };
      }),

      /* --- Reset ---*/
      resetSignupFlow: () => set({
        cats: {},
        activeCatId: "",
        user: defaultUser,
        delivery: defaultDelivery,
        // @TODO: when working on reset flow button don't use default discount
        subscription: defaultSubscription,
        viewState: defaultDirectSalesViewState,
        sufQuizVersion: DEFAULT_DIRECT_SALES_QUIZ_VERSION,
        versionMismatchDetected: false,
        savedAt: dayjs().format(),
        cardReaderId: "",
        targetUser: TargetUser.CUSTOMER,
      }),

      /* --- Version ---*/
      sufQuizVersion: DEFAULT_DIRECT_SALES_QUIZ_VERSION,

      /* --- TTL ---*/
      savedAt: dayjs().format(),
    }),
    {
      name: "direct-sales-suf-store",
      onRehydrateStorage: () => (state, rehydrationError) => {
        if (!state) {
          console.error("[ds-store] Storage rehydration failed:", rehydrationError);
          queueMicrotask(() => {
            useDirectSalesFlowStore.setState({ isStoreInitialised: true });
          });
          return;
        }

        try {
          // --- 1. TTL — clear stale local state if savedAt exists and is older than DIRECT_SALES_FLOW_TTL_DAYS.
          if (
            state.savedAt &&
            dayjs(state.savedAt).isBefore(dayjs().subtract(DIRECT_SALES_FLOW_TTL_DAYS, "day"))
          ) {
            state.resetSignupFlow();
            useDirectSalesFlowErrorStore.getState().clearAll();
            queueMicrotask(() => state.setIsStoreInitialised(true));
            return;
          }

          // --- 2. Parse persisted snapshot with Zod for validation and normalisation.
          const persistedSlice = {
            isStoreInitialised: state.isStoreInitialised,
            versionMismatchDetected: state.versionMismatchDetected,
            cats: state.cats,
            activeCatId: state.activeCatId,
            user: state.user,
            delivery: state.delivery,
            subscription: state.subscription,
            viewState: state.viewState,
            isLoading: state.isLoading,
            sufQuizVersion: state.sufQuizVersion,
            savedAt: state.savedAt,
            cardReaderId: state.cardReaderId,
            targetUser: state.targetUser,
          };

          const parsed = parseDirectSalesPersistedSnapshot(persistedSlice);
          if (!parsed.success) {
            // Corrupt or incompatible persisted shape — reset store and clear errors.
            state.resetSignupFlow();
            useDirectSalesFlowErrorStore.getState().clearAll();
            queueMicrotask(() => state.setIsStoreInitialised(true));
            return;
          }

          // --- 3. Apply parsed state to the store on success.
          // Check for version mismatch and set flag (don't reset here).
          // Reset logic is handled explicitly in Layout useEffect.
          const next: TDirectSalesStoreData = {
            ...parsed.data,
            ...(parsed.data.sufQuizVersion !== DEFAULT_DIRECT_SALES_QUIZ_VERSION && {
              versionMismatchDetected: true,
            }),
          };

          // --- 4. Defer setState so the store variable is assigned (avoids TDZ during create())
          // and defer savedAt reset and setIsStoreInitialised so sync persist doesn't overwrite them.
          queueMicrotask(() => {
            useDirectSalesFlowStore.setState({
              ...next,
              savedAt: dayjs().format(),
            } as Partial<IDirectSalesSignupFlowStore>);
            state.setIsStoreInitialised(true);
          });
        } catch (error) {
          console.error("[ds-store] Rehydration failed:", error);
          state.resetSignupFlow();
          useDirectSalesFlowErrorStore.getState().clearAll();
          queueMicrotask(() => state.setIsStoreInitialised(true));
        }
      },
    },
  ),
);
